import { getSceneActions, getSceneState } from "../stores/sceneStore";
import { useSelectionStore } from "../stores/selectionStore";
import { usePhysicsStore } from "../stores/physicsStore";
import { loadLibraryAsset, libraryLoadThrowIfAborted } from "./libraryAssetLoader";
import { getLibraryAssetByKey, getLibraryAssetKey, LIBRARY_ITEMS } from "./libraryCatalog";
import { loadLibraryRecipeStep } from "./sceneRecipes";
import { pickShallowAttachmentSiteUuidForWorldRoot } from "./libraryGripperAttachTargets";
import { isPhysicsAuthoringLocked, shouldBlockSceneAuthoringMutation } from "./physicsGuards";
import { startSimulationFromCurrentFiles, stopSimulation } from "../physics/mujocoBridge";
import { logger } from "./logger";

const DEFAULT_ORIENTATION = [1, 0, 0, 0];

const restartPhysicsIfNeeded = async (wasPhysicsRunning, sourceLabel) => {
  if (!wasPhysicsRunning) return;
  try {
    await stopSimulation({ restoreScene: false });
    await new Promise((resolve) => setTimeout(resolve, 50));
    await startSimulationFromCurrentFiles({ preserveActuatorControls: true });
  } catch (restartError) {
    logger.warn(`[ObjectLibrary] Failed to restart physics after ${sourceLabel} add`, restartError);
  }
};

/**
 * Remove library roots inserted during a cancelled load. Stops the MuJoCo worker when needed so
 * `removeBody` is not blocked by physics guards.
 */
const rollbackInsertedLibraryRoots = async (insertedRoots, physicsWasRunning) => {
  const list = [...insertedRoots].filter(Boolean).reverse();
  if (list.length === 0) return;
  let stoppedSimulation = false;
  if (isPhysicsAuthoringLocked()) {
    try {
      await stopSimulation({ restoreScene: false });
      stoppedSimulation = true;
      await new Promise((resolve) => setTimeout(resolve, 50));
    } catch (error) {
      logger.warn("[ObjectLibrary] stopSimulation failed while cancelling library load", error);
    }
  }
  const sceneActions = getSceneActions();
  for (const uuid of list) {
    sceneActions.removeBody(uuid);
  }
  if (stoppedSimulation && physicsWasRunning) {
    await restartPhysicsIfNeeded(true, "library-load-cancelled");
  }
};

/** Position-only override: orientation stays the asset / scene default (see normalizeTransformOverride). */
const toTransformOverride = (position) => {
  if (!Array.isArray(position) || position.length !== 3) return null;
  const safePosition = position.map((value) => Number(value));
  if (!safePosition.every(Number.isFinite)) return null;
  return { position: safePosition };
};

/**
 * Resolve a follow-on catalog entry. `getLibraryAssetKey` alone is not enough when
 * `xmlFilename` is omitted because it defaults to `${id}.xml`, which does not match
 * assets like Robotiq (`2f85.xml` vs `robotiq_2f85.xml`).
 */
const resolveFollowOnLibraryAsset = (spec) => {
  if (!spec?.id || !spec?.folder) return null;
  if (spec.xmlFilename) {
    const key = getLibraryAssetKey({
      folder: spec.folder,
      id: spec.id,
      xmlFilename: spec.xmlFilename,
    });
    const byKey = getLibraryAssetByKey(key);
    if (byKey) return byKey;
  }
  return (
    LIBRARY_ITEMS.find((asset) => asset.folder === spec.folder && asset.id === spec.id) || null
  );
};

export const placeLibraryObject = async (object, options = {}) => {
  if (shouldBlockSceneAuthoringMutation("placeLibraryObject")) return null;
  const signal = options.signal ?? null;
  const insertedRootsForCancel = [];
  const onInsertedLibraryRoot = (uuid) => {
    if (uuid) insertedRootsForCancel.push(uuid);
  };
  const transformOverride = toTransformOverride(options.position);
  const followOnSpecs = Array.isArray(object?.followOnLibraryAssets)
    ? object.followOnLibraryAssets
    : [];
  const followOnAssets = followOnSpecs
    .map((spec) => {
      const resolved = resolveFollowOnLibraryAsset(spec);
      if (!resolved) {
        logger.warn(
          "[ObjectLibrary] Unknown follow-on library asset (check folder/id/xmlFilename):",
          spec
        );
      }
      return resolved;
    })
    .filter(Boolean);

  if (followOnSpecs.length > 0 && followOnAssets.length !== followOnSpecs.length) {
    logger.warn(
      "[ObjectLibrary] Some follow-on library assets failed to resolve; gripper or extras may be missing."
    );
  }

  const baseObject = { ...object };
  delete baseObject.followOnLibraryAssets;

  const wasPhysicsRunning = usePhysicsStore.getState().isRunning;
  const soloGripperHoverSite = options.robotAttachmentSiteUuid;
  const deferPhysicsRestart =
    (followOnAssets.length > 0 || !!soloGripperHoverSite) && wasPhysicsRunning;
  const onProgress = options.onProgress;
  const totalUnits = 1 + followOnAssets.length;
  let step = 0;
  const reportStep = (localT) => {
    if (typeof onProgress !== "function") return;
    onProgress((step + localT) / totalUnits);
  };

  try {
    libraryLoadThrowIfAborted(signal);
    const primaryResult = await loadLibraryAsset(baseObject, {
      transformOverride,
      restartPhysics: !deferPhysicsRestart,
      onProgress: reportStep,
      signal,
      onInsertedLibraryRoot,
    });
    step += 1;

    let lastFollowOnRootBodyUuid = null;
    for (const followAsset of followOnAssets) {
      libraryLoadThrowIfAborted(signal);
      // Same path as preset `libraryRecipe` steps after the first robot load.
      const followLoadResult = await loadLibraryRecipeStep(
        { assetKey: getLibraryAssetKey(followAsset) },
        { onProgress: reportStep, signal, onInsertedLibraryRoot }
      );
      step += 1;
      if (followLoadResult?.rootBodyUuid) {
        lastFollowOnRootBodyUuid = followLoadResult.rootBodyUuid;
      }
    }

    const shouldAttachGripperFollowOn =
      followOnAssets.length > 0 &&
      followOnAssets.some((a) => a.folder === "attachments") &&
      primaryResult?.rootBodyUuid &&
      lastFollowOnRootBodyUuid;
    if (shouldAttachGripperFollowOn) {
      try {
        const sceneActions = getSceneActions();
        const bodies = getSceneState().bodies || {};
        const allSites = sceneActions.findAttachmentSites("attachment_site") || [];
        const gripperSiteUuid = pickShallowAttachmentSiteUuidForWorldRoot(
          bodies,
          allSites,
          lastFollowOnRootBodyUuid,
          "attachment_site"
        );
        let robotSiteUuid = pickShallowAttachmentSiteUuidForWorldRoot(
          bodies,
          allSites,
          primaryResult.rootBodyUuid,
          null
        );
        if (options.robotAttachmentSiteUuid) {
          const hovered = allSites.find((s) => s.siteUuid === options.robotAttachmentSiteUuid);
          if (hovered && String(hovered.worldBodyUuid) === String(primaryResult.rootBodyUuid)) {
            robotSiteUuid = options.robotAttachmentSiteUuid;
          }
        }
        const attached = sceneActions.attachBySites(
          lastFollowOnRootBodyUuid,
          primaryResult.rootBodyUuid,
          "attachment_site",
          gripperSiteUuid || undefined,
          robotSiteUuid || undefined
        );
        if (!attached) {
          logger.warn(
            "[ObjectLibrary] attachBySites failed after robot + gripper follow-on load (check attachment_site on both)."
          );
        }
      } catch (error) {
        logger.warn("[ObjectLibrary] attachBySites threw after follow-on gripper load", error);
      }
    }

    // soloGripperHoverSite already extracted above
    if (
      soloGripperHoverSite &&
      followOnAssets.length === 0 &&
      baseObject?.category === "attachment" &&
      primaryResult?.rootBodyUuid
    ) {
      try {
        const sceneActions = getSceneActions();
        const bodies = getSceneState().bodies || {};
        const allSites = sceneActions.findAttachmentSites("attachment_site") || [];
        const hoveredRobotSite = allSites.find((s) => s.siteUuid === soloGripperHoverSite);
        if (hoveredRobotSite?.worldBodyUuid) {
          const gripperSiteUuid = pickShallowAttachmentSiteUuidForWorldRoot(
            bodies,
            allSites,
            primaryResult.rootBodyUuid,
            "attachment_site"
          );
          const attached = sceneActions.attachBySites(
            primaryResult.rootBodyUuid,
            hoveredRobotSite.worldBodyUuid,
            "attachment_site",
            gripperSiteUuid || undefined,
            soloGripperHoverSite
          );
          if (!attached) {
            logger.warn(
              "[ObjectLibrary] Solo gripper attachBySites failed (check attachment_site on gripper and robot)."
            );
          }
        }
      } catch (error) {
        logger.warn("[ObjectLibrary] Solo gripper attachBySites threw", error);
      }
    }

    if (deferPhysicsRestart) {
      await restartPhysicsIfNeeded(true, "library-robot-with-followons");
    }

    return primaryResult;
  } catch (e) {
    if (e?.name === "AbortError") {
      await rollbackInsertedLibraryRoots(insertedRootsForCancel, wasPhysicsRunning);
      useSelectionStore.getState().selectBody(null);
      return null;
    }
    throw e;
  }
};

export const placeMeshAsset = async (entry, options = {}) => {
  if (!entry?.id) return { insertedBodyUuid: null };
  if (shouldBlockSceneAuthoringMutation("placeMeshAsset")) return { insertedBodyUuid: null };
  const wasPhysicsRunning = usePhysicsStore.getState().isRunning;
  const transformOverride = toTransformOverride(options.position);
  const selectionActions = useSelectionStore.getState();
  try {
    const meshName = String(entry.name || entry.id || "mesh_asset");
    const meshUrl = entry.sources?.url || entry.sources?.blobUrl || meshName;

    const actions = getSceneActions();
    const meshUuid = actions.addMesh({
      name: meshName,
      url: meshUrl,
      fingerprint: entry.id,
    });

    actions.importMeshes([meshUuid]);

    const bodies = getSceneState().bodies || {};
    const insertedBodyUuid =
      Object.values(bodies).find(
        (body) =>
          body &&
          Array.isArray(body.geoms) &&
          body.geoms.some((geom) => geom && geom.mesh === meshUuid)
      )?.uuid || null;

    if (insertedBodyUuid && transformOverride) {
      const body = getSceneState().bodies?.[insertedBodyUuid];
      const orientation =
        Array.isArray(body?.orientation) && body.orientation.length === 4
          ? [...body.orientation]
          : DEFAULT_ORIENTATION;
      actions.setBodyTransform(insertedBodyUuid, transformOverride.position, orientation);
    }

    if (insertedBodyUuid) {
      selectionActions.setLastSelectionSource("objectLibrary");
      selectionActions.selectBody(null);
      selectionActions.selectBody(insertedBodyUuid);
    }

    await restartPhysicsIfNeeded(wasPhysicsRunning, "mesh-asset");
    return { insertedBodyUuid };
  } catch (error) {
    logger.error("[ObjectLibrary] Failed to load mesh asset into scene", error);
    await restartPhysicsIfNeeded(wasPhysicsRunning, "mesh-asset");
    throw error;
  }
};
